import type { RecordEnvelope } from './records.js';

/**
 * Security & Lock — a LOCAL-FIRST app lock for a single owner.
 *
 * Scope is deliberate and honest: this is a local passphrase lock with idle
 * auto-lock and a vault step-up gate. The passphrase is never stored in plain
 * text — only a PBKDF2 verifier (salt + iterations + derived hash) lives on the
 * device. Server-grade auth, OTP, and trusted-device flows belong to the cloud
 * layer and are intentionally out of scope here.
 */

export type LockStatus = 'unset' | 'locked' | 'unlocked';

/** PBKDF2 verifier persisted locally. Never contains the passphrase itself. */
export interface PassphraseVerifier {
  readonly saltB64: string;
  readonly iterations: number;
  readonly hashB64: string;
}

export interface SecurityConfig {
  readonly verifier: PassphraseVerifier | null;
  /** Auto-lock after this many idle minutes (0 disables idle auto-lock). */
  readonly autoLockMinutes: number;
  /** Require an unlock/step-up before the Finance vault is shown. */
  readonly requireStepUpForVault: boolean;
}

export const DEFAULT_SECURITY_CONFIG: SecurityConfig = {
  verifier: null,
  autoLockMinutes: 15,
  requireStepUpForVault: true,
};

export type SecurityEventType =
  | 'passphrase_set'
  | 'passphrase_cleared'
  | 'unlock'
  | 'failed_unlock'
  | 'lock'
  | 'auto_lock'
  | 'step_up';

export interface SecurityEvent extends RecordEnvelope {
  readonly kind: 'security_event';
  readonly eventType: SecurityEventType;
  readonly at: string;
  readonly detail: string;
}

/** Recommended PBKDF2 work factor for a local lock. */
export const PBKDF2_ITERATIONS = 210_000;

/**
 * Whether an idle session should auto-lock. Pure. A zero/negative idle setting
 * disables auto-lock. nowIso and lastActiveIso are ISO instants.
 */
export function shouldAutoLock(lastActiveIso: string, nowIso: string, autoLockMinutes: number): boolean {
  if (autoLockMinutes <= 0) return false;
  const last = Date.parse(lastActiveIso);
  const now = Date.parse(nowIso);
  if (Number.isNaN(last) || Number.isNaN(now)) return false;
  return now - last >= autoLockMinutes * 60_000;
}

/** Derive lock status from config + the in-memory unlocked flag. Pure. */
export function lockStatus(config: Pick<SecurityConfig, 'verifier'>, unlocked: boolean): LockStatus {
  if (!config.verifier) return 'unset';
  return unlocked ? 'unlocked' : 'locked';
}
